import React, { useLayoutEffect, useRef } from 'react';
import { ArrowRight, BookOpen, Check, ChevronRight, ClipboardCheck, Dumbbell, Layers3, LoaderCircle, MessageCircle, Paperclip, Pencil, Send, Sparkle } from 'lucide-react';
import { TUTOR_DEMO } from '../../data/onboardingTutorDemo';
import { easeInOut, easeOut, mix, span, useDemoTime, useStageScale } from './demoTimeline';
import './tutorDemo.css';

// One real AI Tutor exchange and the real save-to-deck flow, replayed with the waiting cut out.
const STAGE_WIDTH = 480;
const LOOP_MS = 21500;
const STILL_MS = 18500;
const T = {
  typeStart: 500, typeEnd: 2450, send: 2750,
  streamStart: 3500, streamEnd: 9000,
  actions: 9200, saveClick: 10300,
  prepIn: 10400, prepOut: 12000,
  cardAt: [12400, 12800, 13200],
  targetsIn: 13700, newDeckClick: 14400,
  nameStart: 14650, nameEnd: 15650,
  createClick: 16500, savedAt: 17000,
  fadeOut: 20700,
};
const CURSOR = [
  { target: 'send', start: 2050, end: 2650, click: 2750, hide: 3000 },
  { target: 'save', start: 9650, end: 10200, click: 10300, hide: 10600 },
  { target: 'newDeck', start: 13800, end: 14300, click: 14400, hide: null },
  { target: 'create', start: 15900, end: 16400, click: 16500, hide: 16800 },
];

// Tutor markdown, limited to what this answer uses: paragraphs, **bold**, hard breaks,
// numbered steps with nested bullets, and block quotes.
function inline(text, key) {
  return text.split('**').map((part, index) => (index % 2
    ? <strong key={`${key}-${index}`}>{part}</strong>
    : <React.Fragment key={`${key}-${index}`}>{part}</React.Fragment>));
}

function withBreaks(text, key) {
  return text.split(/ {2}\n/).flatMap((segment, index) => (index
    ? [<br key={`${key}-br-${index}`} />, ...inline(segment.trim(), `${key}-${index}`)]
    : inline(segment.trim(), `${key}-${index}`)));
}

function parseAnswer(source) {
  const blocks = [];
  source.split(/\n{2,}/).forEach((chunk) => {
    if (/^\d+\.\s/.test(chunk)) {
      const [first, ...rest] = chunk.split('\n');
      const item = {
        title: first.replace(/^\d+\.\s/, '').trim(),
        body: rest.filter((line) => !/^\s*-\s/.test(line)).map((line) => line.trim()).join('  \n'),
        bullets: rest.filter((line) => /^\s*-\s/.test(line)).map((line) => line.replace(/^\s*-\s/, '')),
      };
      const last = blocks[blocks.length - 1];
      if (last?.type === 'ol') last.items.push(item);
      else blocks.push({ type: 'ol', start: Number(first.match(/^\d+/)[0]), items: [item] });
    } else if (chunk.startsWith('>')) {
      blocks.push({ type: 'quote', text: chunk.replace(/^>\s?/gm, '') });
    } else {
      blocks.push({ type: 'p', text: chunk });
    }
  });
  return blocks;
}

function Answer({ source }) {
  return parseAnswer(source).map((block, index) => {
    if (block.type === 'ol') {
      return (
        <ol key={index} start={block.start}>
          {block.items.map((item, itemIndex) => (
            <li key={itemIndex}>
              <p>{inline(item.title, `t${index}-${itemIndex}`)}{item.body ? <><br />{withBreaks(item.body, `b${index}-${itemIndex}`)}</> : null}</p>
              {item.bullets.length ? <ul>{item.bullets.map((bullet, bulletIndex) => <li key={bulletIndex}>{inline(bullet, `u${index}-${itemIndex}-${bulletIndex}`)}</li>)}</ul> : null}
            </li>
          ))}
        </ol>
      );
    }
    if (block.type === 'quote') return <blockquote key={index}>{withBreaks(block.text, `q${index}`)}</blockquote>;
    return <p key={index}>{withBreaks(block.text, `p${index}`)}</p>;
  });
}

function streamed(text, progress) {
  if (progress >= 1) return text;
  const cut = Math.floor(text.length * progress);
  const space = text.lastIndexOf(' ', cut);
  return text.slice(0, space > 0 ? space : cut);
}

export default function TutorDemo({ className = '', freezeAt = null }) {
  const frameRef = useRef(null);
  const stageRef = useRef(null);
  const viewportRef = useRef(null);
  const threadRef = useRef(null);
  const cursorRef = useRef(null);
  const targetRefs = { send: useRef(null), save: useRef(null), newDeck: useRef(null), create: useRef(null) };
  const scrollState = useRef({ t: -1, y: 0 });
  const scale = useStageScale(frameRef, STAGE_WIDTH);
  const t = useDemoTime({ frameRef, loopMs: LOOP_MS, stillMs: STILL_MS, freezeAt });
  const { course, mode, welcome, question, answer, cards, deckName } = TUTOR_DEMO;

  const typed = t >= T.send ? '' : question.slice(0, Math.floor(question.length * span(t, T.typeStart, T.typeEnd)));
  const thinking = t >= T.send && t < T.streamStart + 250;
  const bubble = easeOut(span(t, T.send + 60, T.send + 420));
  const welcomeIn = easeOut(span(t, 0, 400)) * (1 - easeInOut(span(t, T.send - 100, T.send + 200)));
  const answerText = t >= T.streamStart ? streamed(answer, span(t, T.streamStart, T.streamEnd)) : '';
  const actions = easeOut(span(t, T.actions, T.actions + 350));
  const panelOpen = t >= T.saveClick + 80 && t < T.savedAt + 380;
  const panelIn = easeOut(span(t, T.saveClick + 80, T.saveClick + 420)) * (1 - easeInOut(span(t, T.savedAt, T.savedAt + 380)));
  const preparing = t < T.prepOut;
  const prepProgress = easeInOut(span(t, T.prepIn, T.prepOut));
  const targetsIn = easeOut(span(t, T.targetsIn, T.targetsIn + 350));
  const newDeck = t >= T.newDeckClick;
  const typedName = deckName.slice(0, Math.floor(deckName.length * span(t, T.nameStart, T.nameEnd)));
  const canCreate = typedName === deckName;
  const saving = t >= T.createClick && t < T.savedAt;
  const saved = t >= T.savedAt;
  const success = easeOut(span(t, T.savedAt + 250, T.savedAt + 650));
  const out = easeInOut(span(t, T.fadeOut, T.fadeOut + 700));
  const pressed = (at) => t >= at - 60 && t < at + 150;

  // Pin the thread to its newest content like the real Tutor, and steer the cursor to live targets.
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const thread = threadRef.current;
    const stage = stageRef.current;
    if (!viewport || !thread || !stage) return;
    const target = Math.max(0, thread.offsetHeight - viewport.clientHeight);
    const previous = scrollState.current;
    const elapsed = t - previous.t;
    const y = elapsed < 0 || elapsed > 400 ? target : previous.y + (target - previous.y) * (1 - Math.exp(-elapsed / 150));
    scrollState.current = { t, y };
    thread.style.transform = `translateY(${-y}px)`;

    const cursor = cursorRef.current;
    if (!cursor) return;
    const stageBox = stage.getBoundingClientRect();
    const ratio = stageBox.width / STAGE_WIDTH || 1;
    const point = (name) => {
      const box = targetRefs[name].current?.getBoundingClientRect();
      if (!box) return null;
      return { x: (box.left - stageBox.left + box.width * 0.55) / ratio, y: (box.top - stageBox.top + box.height * 0.6) / ratio };
    };
    let shown = null;
    CURSOR.forEach((step, index) => {
      const next = CURSOR[index + 1];
      const hideAt = step.hide ?? next?.start ?? step.click + 300;
      if (t >= step.start - 250 && t < hideAt + 250) shown = { step, index };
    });
    if (!shown) {
      cursor.style.opacity = '0';
      return;
    }
    const { step, index } = shown;
    const to = point(step.target);
    const previousStep = CURSOR[index - 1];
    const chained = previousStep && previousStep.hide === null;
    const from = chained ? point(previousStep.target) : to && { x: to.x + 70, y: to.y + 60 };
    if (!to || !from) {
      cursor.style.opacity = '0';
      return;
    }
    const move = easeInOut(span(t, step.start, step.end));
    const fadeIn = chained ? 1 : span(t, step.start - 250, step.start);
    const fadeOut = step.hide ? 1 - span(t, step.hide, step.hide + 250) : 1;
    const press = Math.max(0, 1 - Math.abs(t - step.click) / 120);
    cursor.style.opacity = String(Math.min(fadeIn, fadeOut));
    cursor.style.transform = `translate(${mix(from.x, to.x, move)}px, ${mix(from.y, to.y, move)}px) scale(${1 - press * 0.14})`;
  });

  return (
    <div ref={frameRef} className={`tdm-frame ${className}`.trim()} role="img" aria-label={`The AI Tutor answering a real ${course} question, then saving the answer as ${cards.length} flashcards in a new deck`}>
      <div ref={stageRef} className="tdm-stage" style={{ transform: `scale(${scale})`, opacity: 1 - out }}>
        <header className="tdm-top">
          <span className="tdm-title"><MessageCircle aria-hidden="true" />AI Tutor</span>
          <span className="tdm-mode"><BookOpen aria-hidden="true" />{mode}</span>
          <span className="tdm-course">{course}</span>
        </header>

        <div ref={viewportRef} className="tdm-viewport">
          {welcomeIn > 0 ? (
            <div className="tdm-welcome" style={{ opacity: welcomeIn, transform: `translateY(${mix(0, -10, 1 - welcomeIn)}px)` }}>
              <Sparkle aria-hidden="true" />
              <strong>{welcome.greeting}</strong>
              <p>{welcome.subtitle}</p>
              <ol>{welcome.options.map((option, index) => <li key={option}><b>{String(index + 1).padStart(2, '0')}</b>{option}<ChevronRight aria-hidden="true" /></li>)}</ol>
            </div>
          ) : null}
          <div ref={threadRef} className="tdm-thread">
            {bubble > 0 ? <p className="tdm-user" style={{ opacity: bubble, transform: `translateY(${mix(10, 0, bubble)}px)` }}>{question}</p> : null}
            {answerText ? <div className="tdm-answer"><Answer source={answerText} /></div> : null}
            {actions > 0 ? (
              <div className="tdm-actions" style={{ opacity: actions }}>
                <div className="tdm-action-row">
                  <span ref={targetRefs.save} className={`tdm-action ${saved ? 'is-complete' : ''} ${pressed(T.saveClick) ? 'is-pressed' : ''}`}>
                    {saved ? <Check aria-hidden="true" /> : <Layers3 aria-hidden="true" />}{panelOpen && preparing ? 'Preparing cards…' : saved ? 'Saved' : 'Save to deck'}
                  </span>
                  <span className="tdm-action"><Dumbbell aria-hidden="true" />Practice this skill</span>
                  <span className="tdm-action"><ClipboardCheck aria-hidden="true" />Score an answer</span>
                </div>
                {saved ? (
                  <div className="tdm-success" style={{ opacity: success, transform: `translateY(${mix(6, 0, success)}px)` }}>
                    <span><Check aria-hidden="true" />Saved {cards.length} cards to Flashcards</span><b>View deck</b>
                  </div>
                ) : null}
                {panelOpen ? (
                  <div className="tdm-panel" style={{ opacity: panelIn, transform: `translateY(${mix(10, 0, panelIn)}px)` }}>
                    <div className="tdm-panel-head">
                      <div>
                        <strong>{preparing ? 'Save to deck' : 'Review your flashcards'}</strong>
                        {!preparing ? <span>Check each card, then choose where to save it.</span> : null}
                      </div>
                      <span className="tdm-cancel">Cancel</span>
                    </div>
                    {preparing ? (
                      <div className="tdm-prep">
                        <div className="tdm-prep-art" aria-hidden="true">
                          <span className="is-back" />
                          <span className="is-middle" />
                          <div className="is-front" style={{ transform: `translateY(${Math.sin(t / 420) * 3}px)` }}><Layers3 /><i /><i /><i /></div>
                        </div>
                        <strong>Turning this answer into flashcards</strong>
                        <p>Picking out the key ideas and making each card easy to revise.</p>
                        <div className="tdm-prep-activity"><LoaderCircle aria-hidden="true" style={{ transform: `rotate(${t * 0.36}deg)` }} />Preparing cards</div>
                        <div className="tdm-prep-track"><span style={{ transform: `scaleX(${prepProgress})` }} /></div>
                        <ol className="tdm-prep-steps"><li className="is-current">Prepare</li><li><ArrowRight aria-hidden="true" />Review</li><li><ArrowRight aria-hidden="true" />Save to deck</li></ol>
                      </div>
                    ) : (
                      <>
                        <div className="tdm-preview-head"><strong>{cards.length} cards prepared</strong><span>Ready to review</span></div>
                        {cards.map((card, index) => {
                          const appear = easeOut(span(t, T.cardAt[index], T.cardAt[index] + 380));
                          if (appear <= 0) return null;
                          return (
                            <article key={card.front} className="tdm-card" style={{ opacity: appear, transform: `translateY(${mix(10, 0, appear)}px)` }}>
                              <div className="tdm-card-head"><strong>Card {index + 1}</strong><span><Pencil aria-hidden="true" />Edit</span><span className="is-remove">Remove</span></div>
                              <section><span className="tdm-side">Question</span><p>{inline(card.front, `f${index}`)}</p></section>
                              <section><span className="tdm-side">Answer</span><p>{inline(card.back, `k${index}`)}</p></section>
                            </article>
                          );
                        })}
                        {targetsIn > 0 ? (
                          <div className="tdm-save" style={{ opacity: targetsIn }}>
                            <div className="tdm-targets">
                              <span className={newDeck ? '' : 'is-active'}>Existing deck</span>
                              <span ref={targetRefs.newDeck} className={newDeck ? 'is-active' : ''}>New deck</span>
                            </div>
                            {newDeck ? (
                              <div className="tdm-field">
                                <span>New deck name</span>
                                <div className={`tdm-input ${typedName ? 'has-value' : ''}`}>{typedName || 'e.g. Cell transport'}{t >= T.nameStart && t < T.createClick ? <i className="tdm-caret" style={{ opacity: Math.floor(t / 420) % 2 ? 0 : 1 }} /> : null}</div>
                              </div>
                            ) : null}
                            <div className="tdm-submit">
                              <span ref={targetRefs.create} className={`tdm-create ${canCreate ? 'is-ready' : ''} ${pressed(T.createClick) ? 'is-pressed' : ''}`}>{saving ? 'Saving…' : `Create deck and save ${cards.length}`}</span>
                            </div>
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        <div className="tdm-composer">
          <Paperclip aria-hidden="true" />
          <p className={typed ? 'has-value' : ''}>{typed || 'What would you like to understand?'}</p>
          <span ref={targetRefs.send} className={`tdm-send ${typed.length === question.length || thinking ? 'is-ready' : ''} ${pressed(T.send) ? 'is-pressed' : ''}`}><Send aria-hidden="true" />{thinking ? 'Thinking…' : 'Send'}</span>
        </div>

        <svg ref={cursorRef} className="tdm-cursor" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 3l14 8.2-6.4 1.3 3.7 7.1-2.6 1.3-3.6-7.1L5 18.6z" /></svg>
      </div>
    </div>
  );
}
